import React from "react";
import { Form, Spinner } from "react-bootstrap";

interface Album {
  id: number;
  name: string;
  description: string | null;
  coverPhotoUrl: string | null;
  eventDate: string | null;
  sortOrder: number;
  isPublished: number;
  photoCount: number;
}

interface Props {
  albums: Album[];
  loading: boolean;
  onEdit: (album: Album) => void;
  onDelete: (album: Album) => void;
  onTogglePublish: (album: Album) => void;
  onOpen: (album: Album) => void;
}

const actionLinkStyle: React.CSSProperties = {
  background: "none",
  border: "none",
  padding: 0,
  fontSize: 13,
  cursor: "pointer",
  color: "var(--text-muted)",
};

const AlbumList: React.FC<Props> = ({
  albums,
  loading,
  onEdit,
  onDelete,
  onTogglePublish,
  onOpen,
}) => {
  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (albums.length === 0) {
    return (
      <div className="text-center py-5 text-muted">
        <div style={{ fontSize: 48, marginBottom: 12, color: "#dee2e6" }}>
          <i className="fa fa-image" />
        </div>
        <p>No albums yet. Create your first album to get started.</p>
      </div>
    );
  }

  return (
    <div className="album-cards-grid-admin">
      {albums.map((album) => (
        <div key={album.id} className="album-card-admin">
          {/* Cover / clickable area */}
          <div
            className="album-card-cover-area"
            onClick={() => onOpen(album)}
          >
            {album.coverPhotoUrl ? (
              <img
                src={album.coverPhotoUrl}
                alt={album.name}
                className="album-card-cover-img"
              />
            ) : (
              <div className="album-card-cover-empty">
                <i className="fa fa-image" />
              </div>
            )}
            <div className="album-card-photo-count">
              {album.photoCount} photo{album.photoCount !== 1 ? "s" : ""}
            </div>
          </div>

          {/* Info */}
          <div className="album-card-info">
            <div
              className="album-card-title"
              onClick={() => onOpen(album)}
            >
              {album.name}
            </div>
            {album.eventDate && (
              <div className="album-card-date">
                {new Date(album.eventDate).toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}
              </div>
            )}

            {/* Footer actions */}
            <div className="album-card-actions">
              <div className="d-flex align-items-center gap-1">
                <Form.Check
                  type="switch"
                  checked={!!album.isPublished}
                  onChange={() => onTogglePublish(album)}
                  label=""
                  style={{ marginBottom: 0 }}
                />
                <span style={{ fontSize: 12, color: album.isPublished ? "#198754" : "#adb5bd" }}>
                  {album.isPublished ? "Live" : "Draft"}
                </span>
              </div>
              <div className="d-flex gap-3">
                <button
                  style={actionLinkStyle}
                  onClick={(e) => { e.stopPropagation(); onEdit(album); }}
                >
                  <i className="fa fa-pencil" /> Edit
                </button>
                <button
                  style={{ ...actionLinkStyle, color: "var(--status-danger)" }}
                  onClick={(e) => { e.stopPropagation(); onDelete(album); }}
                >
                  <i className="fa fa-trash" /> Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default AlbumList;
